<script setup lang="ts">
import { VTextField } from '@/modules/shared/components'
import VSelect from './v-select.vue'
import AddressAutocompleteInput from './address-autocomplete-input.vue'
import { useAddressStore } from '@/modules/shared/stores/address-store'
import { type StructuredAddress } from '@/modules/shared/composables/use-address-autocomplete'
import { onMounted, ref, watch } from 'vue'

const props = defineProps<{
  modelValue: any
  v$: any
}>()
const emit = defineEmits(['update:modelValue'])
const addressStore = useAddressStore()

const addressForm = ref(
  props.modelValue || {
    country: '',
    street1: '',
    street2: '',
    city: '',
    state: '',
    zip: '',
    county: '',
  },
)

watch(
  () => addressForm.value.country,
  async (countryCode, prevCountry) => {
    if (!countryCode) return
    await addressStore.fetchStates(countryCode)

    // Only reset when the user switches countries after load
    if (prevCountry && countryCode !== prevCountry) {
      addressForm.value.state = null
    }
  },
)

// this cause problems with is_qsbs_qualified field in spv-new.vue
// watch(
//   addressForm,
//   (newVal) => {
//     emit('update:modelValue', { ...newVal })
//   },
//   { deep: true },
// )

function onAddressSelected(address: StructuredAddress) {
  addressForm.value.street1 = address.street1
  addressForm.value.street2 = address.street2
  addressForm.value.city = address.city
  addressForm.value.state = address.state
  addressForm.value.zip = address.zip
  addressForm.value.county = address.county
  addressForm.value.country = address.country

  if (address.country) {
    addressStore.fetchStates(address.country)
  }
}

onMounted(async () => {
  await Promise.all([addressStore.fetchCountries(), addressStore.fetchStates('US')])
})
</script>

<template>
  <div class="space-y-6">
    <VSelect
      v-model="addressForm.country"
      :clearable="false"
      :disabled="false"
      label="Country"
      :options="addressStore.countryOptions"
      property="country"
      :v$="v$"
    />
    <div class="">
      <AddressAutocompleteInput
        v-model="addressForm.street1"
        label="Address"
        property="street1"
        :v$="v$"
        placeholder="Street"
        :country-code="addressForm.country"
        @select="onAddressSelected"
      />
      <VTextField v-model="addressForm.street2" property="street2" :v$="v$" placeholder="Apartment, suite, etc." />
    </div>
    <div class="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(240px,1fr))]">
      <VTextField v-model="addressForm.city" label="City" property="city" :v$="v$" />
      <VSelect
        v-model="addressForm.state"
        :disabled="false"
        label="State / Province"
        :options="addressStore.stateOptions"
        property="state"
        :v$="v$"
        class="mt-1"
        placeholder="Select..."
      />
      <VTextField v-model="addressForm.zip" label="ZIP / Postal Code" property="zip" :v$="v$" />
      <VTextField v-model="addressForm.county" label="County" property="county" :v$="v$" />
    </div>
  </div>
</template>
